import { Attachment } from '@/modules/Upload/types'
import datacentral from '@/utils/axios/datacentral'
import queryString from 'query-string'
import {
    PartnerContract,
    PartnerContractCreateParams,
    Supplier,
    SupplierCandidateContact,
    SupplierCreateParams,
    SupplierCustomerContract,
} from '../types'
import { ResponseArray, ResponseObject } from '@/types/shared'
import { Subcontract } from '../../Subcontract/types'
import { CreatePartnerSubcontractFormValues } from '@/modals/ContractModals/CreatePartnerSubcontractModal'
import { getContactRoleCandidates } from '@/modules/Customer/services'
import { DataGridPayload } from 'vnetwork-ui/dist/components/GokuDataGrid'

// ============================================================================
// Supplier Services
// ============================================================================

export interface GetSuppliersParams {
    q?: string
    page?: number
    page_size?: number
    sort_by?: string
    sort_order?: 'asc' | 'desc'
    filters?: string
}

export const getSuppliersList = async (
    params: GetSuppliersParams
): Promise<ResponseArray<Supplier>> => {
    const {
        page = 1,
        page_size = 20,
        q = '',
        sort_by,
        sort_order,
        filters,
    } = params

    const queryParams: Record<string, unknown> = { q, page, page_size }

    if (sort_by) {
        queryParams.sort_by = sort_by
        queryParams.sort_order = sort_order || 'desc'
    }

    if (filters) {
        queryParams.filters = filters
    }

    const response = await datacentral.get<ResponseArray<Supplier>>(
        `/partners?${queryString.stringify(queryParams)}`
    )

    if (!response || !response.data) {
        return { results: [], pagination: undefined } as ResponseArray<Supplier>
    }

    return response.data
}

// The "Ứng viên NCC" tab is a worklist: a contact already confirmed as a supplier is done, so it is
// dropped server-side (rows AND total together — filtering client-side would leave the grid's
// rowCount reading the unfiltered pagination.total and break paging). Unlike reseller/affiliate,
// supplier linkage is resolved per CONTACT (partners.customer_contact_id), so confirming one contact
// hides only that contact — the other supplier contacts of the same company stay and remain
// convertible, which is exactly what migration 20260612103000 made possible.
export const getSupplierCandidates = async (
    payload: DataGridPayload
): Promise<ResponseArray<SupplierCandidateContact>> => {
    return getContactRoleCandidates({
        ...payload,
        role: 'supplier',
        exclude_linked: true,
    })
}

export const getSupplier = async (
    partnerID: number
): Promise<ResponseObject<Supplier>> => {
    const response = await datacentral.get<ResponseObject<Supplier>>(
        `/partners/${partnerID}`
    )
    return response?.data
}

export const createSupplier = async ({
    data,
    attachments = [],
}: {
    data: SupplierCreateParams
    attachments: Attachment[]
}) => {
    const response = await datacentral.post(`/partners`, {
        partner: data,
        attachments,
    })
    return response?.data
}

export const updateSupplier = async ({
    partnerID,
    data,
    attachments = [],
    attachments_to_delete = [],
}: {
    partnerID: number
    data: SupplierCreateParams
    attachments: Attachment[]
    attachments_to_delete?: number[]
}) => {
    const response = await datacentral.put(`/partners/${partnerID}`, {
        partner: data,
        attachments,
        attachments_to_delete,
    })
    return response?.data
}

export const deleteSupplier = async (partnerID: number) => {
    const response = await datacentral.delete(`/partners/${partnerID}`)
    return response?.data
}

export const checkSupplierTaxCode = async (params: {
    partner_tax_code: string
    partner_id?: number
}): Promise<{ exists: boolean; message?: string }> => {
    const query = queryString.stringify({
        partner_tax_code: params.partner_tax_code,
        ...(params.partner_id && { partner_id: params.partner_id }),
    })
    const response = await datacentral.get(
        `/suppliers/partners/check/partner-tax-code?${query}`
    )
    return response?.data?.result || { exists: false }
}

export const getSupplierOptions = async (): Promise<
    { partner_id: number; partner_name: string; partner_code: string }[]
> => {
    const response = await datacentral.get(`/partners/options`)
    return response?.data?.results || []
}

export const getSupplierCustomerContracts = async (
    partnerID: number
): Promise<ResponseArray<SupplierCustomerContract>> => {
    const response = await datacentral.get<
        ResponseArray<SupplierCustomerContract>
    >(`/partners/${partnerID}/customer-contracts`)
    return response?.data
}

// ============================================================================
// Partner Contract Services
// ============================================================================

// New paginated API call for DataTableCustomized
export interface GetPartnerContractsParams {
    q?: string
    page?: number
    page_size?: number
    sort_by?: string
    sort_order?: 'asc' | 'desc'
    filters?: string // Base64 encoded filter object
}

export const getPartnerContractsList = async (
    params: GetPartnerContractsParams
): Promise<ResponseArray<PartnerContract>> => {
    const {
        page = 1,
        page_size = 20,
        q = '',
        sort_by,
        sort_order,
        filters,
    } = params

    const queryParams: Record<string, unknown> = { q, page, page_size }

    if (sort_by) {
        queryParams.sort_by = sort_by
        queryParams.sort_order = sort_order || 'desc'
    }

    if (filters) {
        queryParams.filters = filters
    }

    const response = await datacentral.get<ResponseArray<PartnerContract>>(
        `/contracts/partners?${queryString.stringify(queryParams)}`
    )

    if (!response || !response.data) {
        return {
            results: [],
            pagination: undefined,
        } as ResponseArray<PartnerContract>
    }

    return response.data
}

export const createContractPartner = async ({
    data,
    attachments = [],
}: {
    data: PartnerContractCreateParams
    attachments: Attachment[]
}) => {
    const response = await datacentral.post(`/contracts/partners`, {
        contract: data,
        attachments: attachments,
    })

    return response?.data
}
export const updateContractPartner = async ({
    contractID,
    data,
    attachments = [],
    attachments_to_delete = [],
}: {
    contractID: number
    data: PartnerContractCreateParams
    attachments: Attachment[]
    attachments_to_delete?: number[]
}) => {
    const response = await datacentral.put(
        `/contracts/partners/${contractID}`,
        {
            contract: data,
            attachments: attachments,
            attachments_to_delete: attachments_to_delete,
        }
    )

    return response?.data
}

export const getContractPartner = async (contractID: number) => {
    const response = await datacentral.get<ResponseObject<PartnerContract>>(
        `/contracts/partners/${contractID}`
    )
    return response?.data
}

/**
 * Check if contract number already exists
 * @param contract_number_id - The contract number to check
 * @param contract_id - Optional contract ID to exclude (for update mode)
 * @returns { exists: boolean, message?: string }
 */
export const checkPartnerContractNumber = async (params: {
    contract_number_id: string
    contract_id?: number
}): Promise<{ exists: boolean; message?: string }> => {
    const query = queryString.stringify({
        contract_number_id: params.contract_number_id,
        ...(params.contract_id && { contract_id: params.contract_id }),
    })
    const response = await datacentral.get(
        `/contracts/partners/check/contract-number?${query}`
    )
    return response?.data?.result || { exists: false }
}

export const getContractPartnerSubcontracts = async (contractID: number) => {
    const response = await datacentral.get<ResponseArray<Subcontract>>(
        `/contracts/partners/${contractID}/subcontracts`
    )
    return response?.data
}

export const genericPartnerSubcontractCode = async ({
    contractID,
}: {
    contractID: number
}) => {
    const response = await datacentral.get(
        `/contracts/partners/${contractID}/subcontracts/code`
    )

    return response?.data?.result?.subcontract_code as string
}
export const createOrUpdatePartnerSubcontract = async ({
    contractID,
    subcontractID,
    data,
    attachments,
    attachments_to_delete = [],
}: {
    contractID: number
    subcontractID?: number
    data: CreatePartnerSubcontractFormValues
    attachments: Attachment[]
    attachments_to_delete: number[]
}) => {
    const response = await datacentral.post(
        `/contracts/partners/${contractID}/subcontracts${
            subcontractID ? `/${subcontractID}` : ''
        }`,
        {
            subcontract: data.subcontract,
            attachments,
            attachments_to_delete: attachments_to_delete,
        }
    )

    return response?.data?.result
}
